// Componente raíz del bundle del theme login facturadirecta.v3.
//
// Replica el patrón del KeycloakPage del bundle legacy: una sola entrada
// (`runPage(command)`) que decide qué pantalla pintar en función del
// `command` que Keycloak pasa desde el template.ftl. La selección NO se
// hace por URL/router — la decide Keycloak server-side al servir el
// FTL — así que aquí no usamos react-router.
//
// El `data` viene de window.kc_data (poblado por el FTL). Lo metemos en
// useState porque algunos handlers lo mutan (form fields, mensajes per
// field, running flag); el legacy lo hacía mutándolo in-place y haciendo
// setState({data}), aquí cambiamos por setState con clones.

import { createRoot } from "react-dom/client";
import { useEffect, useState, type FormEvent } from "react";
import { applyHints } from "./hint";
import { getKcData } from "./window";
import { getNativeApp, isIOSNativeApp } from "./nativeApp";
import { addHiddenField } from "./addHiddenField";
import { msg } from "./i18n";
import { Logo } from "./components/Logo";
import { Footer } from "./components/Footer";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { LoginResetPasswordPage } from "./pages/LoginResetPasswordPage";
import { LoginUpdatePasswordPage } from "./pages/LoginUpdatePasswordPage";
import { LoginConfigTotpPage } from "./pages/LoginConfigTotpPage";
import { LoginOauthGrantPage } from "./pages/LoginOauthGrantPage";
import { LoginOtpPage } from "./pages/LoginOtpPage";
import { EmailAdviseOtpPage } from "./pages/EmailAdviseOtpPage";
import { EmailOtpPage } from "./pages/EmailOtpPage";
import { DeleteCredentialPage } from "./pages/DeleteCredentialPage";
import { LogoutConfirmPage } from "./pages/LogoutConfirmPage";
import { ErrorPage } from "./pages/ErrorPage";
import { InfoPage } from "./pages/InfoPage";
import { LoginPageExpiredPage } from "./pages/LoginPageExpiredPage";
import { DeviceVerifyUserCodePage } from "./pages/DeviceVerifyUserCodePage";
import { report as reportError } from "./errorReporter";
import type { KcData } from "./kcData";

interface LoginRootProps {
  command: string;
  initialData: KcData;
}

function LoginRoot({ command, initialData }: LoginRootProps) {
  const [data, setData] = useState<KcData>(initialData);
  const [userDidAction, setUserDidAction] = useState(false);
  const [running, setRunning] = useState(false);
  const [showLogoutConfirmForm, setShowLogoutConfirmForm] = useState(false);

  // logout-confirm: al montar, hacer auto-submit del form. Si pasados 2s
  // seguimos en la página (no hubo redirect), mostrar el form.
  useEffect(() => {
    if (command === "logout-confirm") {
      const f = document.getElementById("kc-logout-confirm") as HTMLFormElement | null;
      f?.submit();
      const timer = setTimeout(() => {
        setShowLogoutConfirmForm(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [command]);

  const onUserAction = () => setUserDidAction(true);

  const onChange = (key: string, value: string | boolean) => {
    setData((prev) => ({
      ...prev,
      form: { ...(prev.form ?? {}), [key]: value },
    }));
  };

  // Ayudante: añade un mensaje per-field (key arbitraria — en runtime
  // KC envía cualquier key, no sólo las del tipo declarado).
  const setFieldMessage = (key: string, msg: string) => {
    setData((prev) => ({
      ...prev,
      messagesPerField: {
        ...(prev.messagesPerField as Record<string, string | undefined>),
        [key]: msg,
      } as KcData["messagesPerField"],
    }));
  };

  // Nota arquitectónica sobre los botones submit con name="..." (en
  // particular cancel-aia y accept): NO les ponemos onClick con
  // setState ni `disabled={running}`. En el legacy CoffeeScript se
  // hacía `data.running = true` mutando el objeto sin re-render, por
  // eso funcionaba; en React 18 un setRunning en onClick re-renderiza
  // el botón como disabled ANTES de que el browser serialice los form
  // data, y el par name=value (cancel-aia=true / accept) se queda
  // fuera del POST → la action server-side no se ejecuta. Mantener
  // esos botones enabled todo el tiempo es la única vía limpia.

  const onSubmit = (
    e: FormEvent<HTMLFormElement>,
    iosLoginSubmit?: boolean,
  ) => {
    // AIA cancel: si el submitter es el botón Cancelar (name="cancel-aia"),
    // dejamos que la submission nativa siga sin tocar nada. Si tocáramos
    // running aquí, React re-renderizaría el form con el botón
    // disabled antes de que el browser serialice los form data, y el
    // par cancel-aia=true se quedaría fuera del POST.
    const submitter = (e.nativeEvent as SubmitEvent | undefined)?.submitter;
    if (submitter && (submitter as HTMLButtonElement).name === "cancel-aia") {
      return;
    }

    if (command === "register") {
      // register: validación de legal-terms inline. El checkbox legal se
      // muestra en el paso "email" del flujo OTP y en el formulario de
      // registro clásico, pero NO en el formulario post-OTP (ya se aceptó
      // en el paso "email"). Solo lo exigimos donde se muestra.
      const isEmailStep = data.form?.registrationStep === "email";
      const verifiedEmail =
        (data.form?.verifiedEmail as string | undefined) ?? "";
      const legalShown = isEmailStep || verifiedEmail === "";
      if (!legalShown || data.form?.["accepted-legal-terms"]) {
        setRunning(true);
      } else {
        e.preventDefault();
        setFieldMessage(
          "accepted-legal-terms",
          msg("legalConditionsRequired"),
        );
      }
      return;
    }

    // iOS: submit programático para evitar TouchID autosubmit.
    const nativeAppNow = getNativeApp(window.location.search, data);
    if (
      command === "login" &&
      iosLoginSubmit &&
      userDidAction &&
      isIOSNativeApp(nativeAppNow)
    ) {
      setRunning(true);
      document.forms[0]?.submit();
      return;
    }

    // Validaciones inline para pantallas con campos requeridos. El legacy
    // las hace inspeccionando el DOM en lugar del state porque algunos
    // valores (otp, totp, userLabel) son uncontrolled.
    let mustSubmit = true;
    if (command === "login-config-totp") {
      const userLabel = (document.getElementById("userLabel") as HTMLInputElement | null)?.value;
      const totp = (document.getElementById("totp") as HTMLInputElement | null)?.value;
      if (!userLabel) {
        mustSubmit = false;
        setFieldMessage("userLabel", msg("missingUserLabelMessage"));
      }
      if (!totp) {
        mustSubmit = false;
        setFieldMessage("totp", msg("missingTotpMessage"));
      }
    } else if (command === "login-otp") {
      const otp = (document.getElementById("otp") as HTMLInputElement | null)?.value;
      if (!otp) {
        mustSubmit = false;
        setFieldMessage("otp", msg("missingTotpMessage"));
      }
    }

    if (mustSubmit) {
      setRunning(true);
    } else {
      e.preventDefault();
    }
  };

  // Custom submit: pantallas con botones type="button" que necesitan
  // añadir un hidden field name=action al form antes de submitear, y
  // hacer validación inline. Port directo del onCustomSubmit del
  // legacy.
  const handleCustomSubmit = (
    pageId: "email-otp" | "email-advise-otp" | "login-oauth-grant",
    action: string,
  ): boolean => {
    if (running) return false;

    let mustSubmit = true;
    let formId: string | null = null;

    switch (pageId) {
      case "email-otp": {
        formId = "kc-email-otp-form";
        if (action === "login") {
          const code = (document.getElementById("emailCode") as HTMLInputElement | null)?.value;
          if (!code) {
            mustSubmit = false;
            setFieldMessage("emailCode", msg("missingEmailCodeMessage"));
          }
        } else if (action === "resend" || action === "cancel") {
          addHiddenField(formId, action, action);
        } else {
          mustSubmit = false;
        }
        break;
      }
      case "email-advise-otp": {
        formId = "kc-email-advise-otp-form";
        addHiddenField(formId, action, action);
        break;
      }
      case "login-oauth-grant": {
        formId = "kc-form-actions";
        if (action === "accept" || action === "cancel") {
          addHiddenField(formId, action, action);
        } else {
          mustSubmit = false;
        }
        break;
      }
      default:
        mustSubmit = false;
    }

    if (mustSubmit && formId) {
      setRunning(true);
      const theForm = document.getElementById(formId) as HTMLFormElement | null;
      theForm?.submit();
      return true;
    }
    return false;
  };

  const nativeApp = getNativeApp(window.location.search, data);

  let pageEl: React.ReactNode;
  switch (command) {
    case "login":
      pageEl = (
        <LoginPage
          data={data}
          running={running}
          nativeApp={nativeApp}
          onUserAction={onUserAction}
          onChange={onChange}
          onSubmit={onSubmit}
        />
      );
      break;
    case "register":
      pageEl = (
        <RegisterPage
          data={data}
          running={running}
          nativeApp={nativeApp}
          onChange={onChange}
          onSubmit={onSubmit}
        />
      );
      break;
    case "login-reset-password":
      pageEl = (
        <LoginResetPasswordPage
          data={data}
          running={running}
          nativeApp={nativeApp}
          onSubmit={onSubmit}
        />
      );
      break;
    case "login-update-password":
      pageEl = (
        <LoginUpdatePasswordPage
          data={data}
          running={running}
          onSubmit={onSubmit}
        />
      );
      break;
    case "login-config-totp":
      pageEl = <LoginConfigTotpPage data={data} onSubmit={onSubmit} />;
      break;
    case "login-oauth-grant":
      pageEl = (
        <LoginOauthGrantPage
          data={data}
          running={running}
          onCustomSubmit={(action) =>
            handleCustomSubmit("login-oauth-grant", action)
          }
        />
      );
      break;
    case "login-otp":
      pageEl = (
        <LoginOtpPage data={data} running={running} onSubmit={onSubmit} />
      );
      break;
    case "email-advise-otp":
      pageEl = (
        <EmailAdviseOtpPage
          data={data}
          running={running}
          onCustomSubmit={(action) =>
            handleCustomSubmit("email-advise-otp", action)
          }
        />
      );
      break;
    case "email-otp":
      pageEl = (
        <EmailOtpPage
          data={data}
          running={running}
          onCustomSubmit={(action) => handleCustomSubmit("email-otp", action)}
        />
      );
      break;
    case "delete-credential":
      pageEl = <DeleteCredentialPage data={data} />;
      break;
    case "logout-confirm":
      pageEl = (
        <LogoutConfirmPage
          data={data}
          running={running}
          showLogoutConfirmForm={showLogoutConfirmForm}
          onSubmit={onSubmit}
        />
      );
      break;
    case "error":
      pageEl = <ErrorPage data={data} />;
      break;
    case "info":
      pageEl = <InfoPage data={data} />;
      break;
    case "login-page-expired":
      pageEl = <LoginPageExpiredPage data={data} />;
      break;
    case "login-oauth2-device-verify-user-code":
      pageEl = (
        <DeviceVerifyUserCodePage
          data={data}
          running={running}
          onSubmit={onSubmit}
        />
      );
      break;
    default:
      // Pantallas que el legacy tampoco portaba específicamente
      // (login-update-profile, login-verify-email, login-idp-link-*,
      // code) — mostrar fallback genérico y reportar a GCP.
      reportError(`Pantalla aún no portada en login v3: ${command}`);
      pageEl = (
        <div className="container" style={{ padding: 24 }}>
          <h2>{msg("unsupportedCommandTitle")}</h2>
          <p>
            <span dangerouslySetInnerHTML={{
              __html: msg("unsupportedCommandBodyHtml", command),
            }} />
          </p>
        </div>
      );
  }

  // Footer va FUERA de form-container, como hijo directo del
  // flex-container. Con `form-container { flex: 1 }` y el footer al
  // final, el flex column lo empuja al pie de la página
  // automáticamente. Si lo metiéramos dentro del form-container
  // quedaría pegado al último elemento del form (botón "Empieza
  // gratis", etc.).
  return (
    <div className="flex-container">
      <Logo nativeApp={nativeApp} />
      <div className="form-container">{pageEl}</div>
      <Footer />
    </div>
  );
}

export function runPage(command: string) {
  const container = document.getElementById("content");
  if (!container) {
    console.error(
      'Login v3: no se encuentra <div id="content"> en el template',
    );
    return;
  }
  const kcData = getKcData();
  // Aplicamos hints (?hint_email=…) a form ANTES del primer render.
  const seededForm = applyHints(kcData.form ?? {}, window.location.search);
  const initial: KcData = {
    ...kcData,
    form: { ...seededForm, password: "", "password-confirm": "" },
  };

  const root = createRoot(container);
  root.render(<LoginRoot command={command} initialData={initial} />);
}
